import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { 
  GitBranch,
  Users,
  DollarSign,
  Zap,
  Gift,
  TrendingUp,
  Lock,
  Unlock,
  RefreshCw,
  ShoppingCart,
  Eye,
  Copy
} from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';

const MLMMatrix = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [positions, setPositions] = useState([]);
  const [downline, setDownline] = useState([]);
  const [products, setProducts] = useState([]);
  const [userStats, setUserStats] = useState({
    pif_count: 0,
    phase2_unlocked: false,
    active_positions: 0,
    total_earnings: 0
  });

  useEffect(() => {
    loadMatrixData();
    loadProducts();
  }, []);

  const loadMatrixData = async () => {
    try {
      // Load matrix positions
      const positionsResponse = await fetch(`${API_ENDPOINTS.MEMBER}/matrix/positions`);
      if (positionsResponse.ok) {
        const positionsData = await positionsResponse.json();
        setPositions(positionsData.positions || []);
      }

      // Load downline
      const downlineResponse = await fetch(`${API_ENDPOINTS.MEMBER}/matrix/downline`);
      if (downlineResponse.ok) {
        const downlineData = await downlineResponse.json();
        setDownline(downlineData.downline || []);
      }

      // Update user stats (would come from user profile endpoint)
      setUserStats({
        pif_count: user?.pif_count || 0,
        phase2_unlocked: user?.phase2_unlocked || false,
        active_positions: positions.filter(p => p.status === 'active').length,
        total_earnings: 0 // Calculate from payments
      });

    } catch (error) {
      console.error('Error loading matrix data:', error);
      toast.error('Failed to load matrix data');
    }
  };

  const loadProducts = async () => {
    try {
      // For now, we'll create mock products based on the seed data
      const mockProducts = [
        {
          id: 'phase1-basic',
          name: 'Basic Credit Pack',
          phase: 1,
          level: null,
          price_usd: 5.95,
          credits_awarded: 100,
          description: '1x2 Matrix entry with 100 ad credits'
        },
        {
          id: 'phase2-l1',
          name: 'Rookie Pro Pack',
          phase: 2,
          level: 1,
          price_usd: 15.00,
          credits_awarded: 300,
          description: '12x3 Matrix Level 1 with 300 ad credits'
        },
        {
          id: 'phase2-l2',
          name: 'Pro Pack',
          phase: 2,
          level: 2,
          price_usd: 35.00,
          credits_awarded: 750,
          description: '12x3 Matrix Level 2 with 750 ad credits'
        },
        {
          id: 'phase2-l3',
          name: 'Enterprise Pack',
          phase: 2,
          level: 3,
          price_usd: 100.00,
          credits_awarded: 2000,
          description: '12x3 Matrix Level 3 with 2000 ad credits'
        }
      ];
      setProducts(mockProducts);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const handlePurchase = async (productId, sponsorId = null) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.MEMBER}/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          product_id: productId,
          sponsor_id: sponsorId
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Order created successfully! Proceed to payment.');
        // In a real implementation, this would redirect to NowPayments
        loadMatrixData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create order');
      }
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  const createPIFVoucher = async (productId) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.MEMBER}/pif/vouchers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ product_id: productId })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`PIF voucher created: ${data.voucher_code}`);
        navigator.clipboard.writeText(data.voucher_code);
        loadMatrixData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create PIF voucher');
      }
    } catch (error) {
      console.error('Error creating PIF voucher:', error);
      toast.error('Failed to create PIF voucher');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const MatrixVisualization = ({ position }) => {
    if (!position) return null;

    return (
      <div className="border rounded-lg p-4 bg-gray-50">
        <div className="text-center mb-4">
          <div className="inline-block p-3 bg-blue-100 rounded-full">
            <Users className="h-6 w-6 text-blue-600" />
          </div>
          <p className="font-semibold mt-2">Your Position</p>
          <p className="text-sm text-gray-600">
            Phase {position.phase} - Cycle {position.cycle_count}
          </p>
        </div>

        {position.phase === 1 && (
          <div className="flex justify-center space-x-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                {position.left_child ? (
                  <Users className="h-6 w-6 text-green-600" />
                ) : (
                  <span className="text-gray-400">L</span>
                )}
              </div>
              <p className="text-xs mt-1">Left</p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                {position.right_child ? (
                  <Users className="h-6 w-6 text-green-600" />
                ) : (
                  <span className="text-gray-400">R</span>
                )}
              </div>
              <p className="text-xs mt-1">Right</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Matrix Dashboard</h1>
          <p className="text-gray-600 mt-1">
            Manage your positions, track earnings, and grow your network
          </p>
        </div>
        
        <Button onClick={loadMatrixData} className="btn-primary">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <GitBranch className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{userStats.active_positions}</p>
                <p className="text-gray-600 text-sm">Active Positions</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(userStats.total_earnings)}
                </p>
                <p className="text-gray-600 text-sm">Total Earnings</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Gift className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{userStats.pif_count}</p>
                <p className="text-gray-600 text-sm">PIF Count</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                {userStats.phase2_unlocked ? (
                  <Unlock className="h-6 w-6 text-orange-600" />
                ) : (
                  <Lock className="h-6 w-6 text-orange-600" />
                )}
              </div>
              <div className="ml-4">
                <p className="text-lg font-bold text-gray-900">Phase 2</p>
                <p className="text-gray-600 text-sm">
                  {userStats.phase2_unlocked ? 'Unlocked' : `${12 - userStats.pif_count} PIFs needed`}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="positions" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="positions">My Positions</TabsTrigger>
          <TabsTrigger value="products">Purchase</TabsTrigger>
          <TabsTrigger value="downline">My Downline</TabsTrigger>
          <TabsTrigger value="pif">PIF Tools</TabsTrigger>
        </TabsList>

        {/* My Positions */}
        <TabsContent value="positions" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {positions.length === 0 ? (
              <Card className="lg:col-span-2">
                <CardContent className="text-center py-12">
                  <GitBranch className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No positions yet</h3>
                  <p className="text-gray-600 mb-4">
                    Purchase your first product to enter the matrix
                  </p>
                  <Button className="btn-primary">
                    <ShoppingCart className="h-4 w-4 mr-2" />
                    Browse Products
                  </Button>
                </CardContent>
              </Card>
            ) : (
              positions.map((position) => (
                <Card key={position.id}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">
                          Phase {position.phase} Position
                          {position.level && ` - Level ${position.level}`}
                        </CardTitle>
                        <CardDescription>
                          Created {new Date(position.created_at).toLocaleDateString()}
                        </CardDescription>
                      </div>
                      <Badge 
                        variant={position.status === 'active' ? 'default' : 'secondary'}
                        className={position.status === 'active' ? 'bg-green-100 text-green-800' : ''}
                      >
                        {position.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <MatrixVisualization position={position} />
                    
                    <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Cycle Count:</span>
                        <span className="ml-2 font-semibold">{position.cycle_count}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Order Count:</span>
                        <span className="ml-2 font-semibold">{position.order_count || 0}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Products/Purchase */}
        <TabsContent value="products" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {products.map((product) => (
              <Card key={product.id} className="relative">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg">{product.name}</CardTitle>
                      <CardDescription>{product.description}</CardDescription>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-green-600">
                        {formatCurrency(product.price_usd)}
                      </p>
                      <p className="text-sm text-gray-600">
                        {product.credits_awarded} credits
                      </p>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent>
                  <div className="flex items-center justify-between mb-4">
                    <Badge 
                      variant="outline" 
                      className={product.phase === 1 ? 'border-blue-500 text-blue-700' : 'border-purple-500 text-purple-700'}
                    >
                      Phase {product.phase}
                      {product.level && ` - Level ${product.level}`}
                    </Badge>
                    
                    {product.phase === 2 && !userStats.phase2_unlocked && (
                      <Badge variant="secondary" className="bg-red-100 text-red-800">
                        <Lock className="h-3 w-3 mr-1" />
                        Locked
                      </Badge>
                    )}
                  </div>

                  <div className="flex space-x-2">
                    <Button 
                      className="flex-1 btn-primary"
                      onClick={() => handlePurchase(product.id)}
                      disabled={loading || (product.phase === 2 && !userStats.phase2_unlocked)}
                    >
                      <ShoppingCart className="h-4 w-4 mr-2" />
                      {loading ? 'Processing...' : 'Purchase'}
                    </Button>
                    
                    <Button 
                      variant="outline"
                      onClick={() => createPIFVoucher(product.id)}
                      disabled={loading}
                    >
                      <Gift className="h-4 w-4" />
                    </Button>
                  </div>

                  {product.phase === 2 && !userStats.phase2_unlocked && (
                    <p className="text-xs text-red-600 mt-2">
                      Requires {12 - userStats.pif_count} more PIFs to unlock
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Downline */}
        <TabsContent value="downline" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Your Network</CardTitle>
              <CardDescription>
                People you've sponsored into the matrix
              </CardDescription>
            </CardHeader>
            <CardContent>
              {downline.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No downline members yet</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Share your PIF vouchers to build your network
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {downline.map((member) => (
                    <div key={member.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="font-semibold">{member.user_info?.full_name}</p>
                        <p className="text-sm text-gray-600">{member.user_info?.email}</p>
                        <p className="text-xs text-gray-500">
                          Joined {new Date(member.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <Badge 
                          variant={member.status === 'active' ? 'default' : 'secondary'}
                          className={member.status === 'active' ? 'bg-green-100 text-green-800' : ''}
                        >
                          {member.status}
                        </Badge>
                        <p className="text-sm text-gray-600 mt-1">
                          Phase {member.phase}
                          {member.level && ` L${member.level}`}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* PIF Tools */}
        <TabsContent value="pif" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Pay-It-Forward Tools</CardTitle>
              <CardDescription>
                Create vouchers to help others join the network
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="space-y-4">
                  <h4 className="font-semibold">Quick PIF Actions</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {products.slice(0, 2).map((product) => (
                      <Button
                        key={product.id}
                        variant="outline"
                        className="w-full justify-center"
                        onClick={() => createPIFVoucher(product.id)}
                        disabled={loading}
                      >
                        <Gift className="h-4 w-4 mr-2" />
                        Create {product.name} Voucher
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-semibold">PIF Progress</h4>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium">Phase 2 Progress</span>
                      <span className="text-sm text-gray-600">
                        {userStats.pif_count}/12
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ width: `${Math.min((userStats.pif_count / 12) * 100, 100)}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-600 mt-2">
                      {userStats.phase2_unlocked 
                        ? "Phase 2 unlocked!" 
                        : `${12 - userStats.pif_count} more PIFs needed`
                      }
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MLMMatrix;